import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/lib/auth";
import MarketingLayout from "@/marketing/MarketingLayout";
import Home from "@/marketing/Home";
import About from "@/marketing/About";
import Solutions from "@/marketing/Solutions";
import Services from "@/marketing/Services";
import { IndustriesIndex, IndustryDetail } from "@/marketing/Industries";
import CaseStudies from "@/marketing/CaseStudies";
import Partnership from "@/marketing/Partnership";
import Careers from "@/marketing/Careers";
import Contact from "@/marketing/Contact";
import { LegalPage, FaqPage, ComingSoon, NotFound } from "@/marketing/Static";
import Leads from "@/pages/Leads";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import AppShell from "@/components/AppShell";
import Dashboard from "@/pages/Dashboard";
import ATS from "@/pages/ATS";
import Jobs from "@/pages/Jobs";
import Candidates from "@/pages/Candidates";
import Analytics from "@/pages/Analytics";
import Settings from "@/pages/Settings";
import AICopilotPage from "@/pages/AICopilotPage";
import CandidatePortal from "@/pages/CandidatePortal";
import ClientPortal from "@/pages/ClientPortal";
import Payroll from "@/pages/Payroll";
import Timesheets from "@/pages/Timesheets";
import Learning from "@/pages/Learning";
import Vendors from "@/pages/Vendors";
import AuditLog from "@/pages/AuditLog";
import EmployeePortal from "@/pages/EmployeePortal";

function Protected({ children, roles }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen grid place-items-center text-sm text-muted-foreground">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (roles && !roles.includes(user.role) && user.role !== "admin") return <Navigate to="/app/dashboard" replace />;
  return children;
}

function RoleRouter() {
  const { user } = useAuth();
  if (!user) return <Navigate to="/login" replace />;
  if (user.role === "candidate") return <Navigate to="/app/candidate" replace />;
  if (user.role === "client") return <Navigate to="/app/client" replace />;
  return <Navigate to="/app/dashboard" replace />;
}

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          <Route element={<MarketingLayout />}>
            <Route path="/" element={<Home />} />
            <Route path="/solutions" element={<Solutions />} />
            <Route path="/services" element={<Services />} />
            <Route path="/industries" element={<IndustriesIndex />} />
            <Route path="/industries/:slug" element={<IndustryDetail />} />
            <Route path="/case-studies" element={<CaseStudies />} />
            <Route path="/partnership" element={<Partnership />} />
            <Route path="/careers" element={<Careers />} />
            <Route path="/about" element={<About />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/faq" element={<FaqPage />} />
            <Route path="/privacy-policy" element={<LegalPage slug="privacy-policy" />} />
            <Route path="/terms" element={<LegalPage slug="terms" />} />
            <Route path="/accessibility" element={<LegalPage slug="accessibility" />} />
            <Route path="/resources" element={<ComingSoon title="Resources" desc="Guides, checklists and templates for technology and workforce leaders." />} />
            <Route path="/insights" element={<ComingSoon title="Insights" desc="Perspectives on AI, cloud, data, automation and the future of work." />} />
            <Route path="/jobs" element={<Navigate to="/careers#positions" replace />} />
            <Route path="/it-staffing" element={<Navigate to="/industries/technology-staffing" replace />} />
            <Route path="/recruitment-services" element={<Navigate to="/solutions#workforce-transformation" replace />} />
            <Route path="/why-choose-us" element={<Navigate to="/about" replace />} />
            <Route path="/404" element={<NotFound />} />
            <Route path="*" element={<NotFound />} />
          </Route>
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/app" element={<Protected><AppShell /></Protected>}>
            <Route index element={<RoleRouter />} />
            <Route path="dashboard" element={<Protected roles={["recruiter", "admin"]}><Dashboard /></Protected>} />
            <Route path="ats" element={<Protected roles={["recruiter", "admin"]}><ATS /></Protected>} />
            <Route path="jobs" element={<Jobs />} />
            <Route path="candidates" element={<Protected roles={["recruiter", "admin"]}><Candidates /></Protected>} />
            <Route path="analytics" element={<Protected roles={["recruiter", "admin", "client"]}><Analytics /></Protected>} />
            <Route path="ai" element={<Protected roles={["recruiter", "admin"]}><AICopilotPage /></Protected>} />
            <Route path="candidate" element={<Protected roles={["candidate"]}><CandidatePortal /></Protected>} />
            <Route path="client" element={<Protected roles={["client"]}><ClientPortal /></Protected>} />
            <Route path="employee" element={<Protected roles={["candidate", "recruiter", "admin"]}><EmployeePortal /></Protected>} />
            <Route path="payroll" element={<Protected roles={["admin"]}><Payroll /></Protected>} />
            <Route path="timesheets" element={<Protected roles={["admin", "recruiter"]}><Timesheets /></Protected>} />
            <Route path="learning" element={<Learning />} />
            <Route path="vendors" element={<Protected roles={["admin", "recruiter"]}><Vendors /></Protected>} />
            <Route path="audit" element={<Protected roles={["admin"]}><AuditLog /></Protected>} />
            <Route path="leads" element={<Protected roles={["admin", "recruiter"]}><Leads /></Protected>} />
            <Route path="settings" element={<Settings />} />
          </Route>
        </Routes>
      </BrowserRouter>
      <Toaster position="top-right" richColors />
    </AuthProvider>
  );
}
